import { isValidISODateTime } from 'helpers/dateTime.helper/dateTime.helper'
import type { ElplanDayTableCellFault, ElplanDayTableCellFaultSpan } from './elplan.cellFault.types'
import { getElplanErrorIdentity, isRecord } from './elplan.errorBody.helper'
import { readOptionalFiniteNumber, readOptionalString } from './elplan.errorDetails.helper'

const ELPLAN_DA_TABLE_INVALID_CELLS_CODE = 'ELPLAN_DA_TABLE_INVALID_CELLS'

export const SPAN_SHORTER_THAN_MINIMUM_CODE = 'SPAN_SHORTER_THAN_MINIMUM'
export const PRICE_REQUIRED_CODE = 'PRICE_REQUIRED'
export const VOLUME_PRECISION_TOO_HIGH_CODE = 'VOLUME_PRECISION_TOO_HIGH'

const FRACTIONAL_SECONDS = /\.\d+(?=([+-]\d{2}:?\d{2}|Z)$)/

function isReadableFaultStartTime(value: string): boolean {
  return isValidISODateTime(value.replace(FRACTIONAL_SECONDS, '')) && Number.isFinite(new Date(value).getTime())
}

function readSpan(cell: Record<string, unknown>): ElplanDayTableCellFaultSpan | undefined {
  const bidType = readOptionalString(cell, 'bid_type')
  const minimumHours = readOptionalFiniteNumber(cell, 'minimum_hours')
  const spanHours = readOptionalFiniteNumber(cell, 'span_hours')
  if (bidType === undefined || minimumHours === undefined || spanHours === undefined) {
    return undefined
  }
  return { bidType, minimumHours, spanHours }
}

export function readElplanDayTableCellFaults(error: unknown): ElplanDayTableCellFault[] {
  const identity = getElplanErrorIdentity(error)
  if (identity.code !== ELPLAN_DA_TABLE_INVALID_CELLS_CODE || !Array.isArray(identity.details?.cells)) {
    return []
  }

  return identity.details.cells.flatMap((cell) => {
    if (!isRecord(cell)) return []

    const tag = readOptionalFiniteNumber(cell, 'tag')
    const code = readOptionalString(cell, 'code')
    const message = readOptionalString(cell, 'message')
    if (tag === undefined || code === undefined) return []

    const rawStartTime = cell.start_time
    if (rawStartTime !== null && (typeof rawStartTime !== 'string' || !isReadableFaultStartTime(rawStartTime))) {
      return []
    }

    return [
      {
        tag,
        startTime: rawStartTime as ISODateTime | null,
        code,
        message,
        span: readSpan(cell),
      },
    ]
  })
}

export function isBidSpanCellFault(fault: ElplanDayTableCellFault): boolean {
  return fault.code === SPAN_SHORTER_THAN_MINIMUM_CODE
}
